import 'isomorphic-fetch';
import { useState } from 'react';
import { useAuthMutate } from '../../auth/authHooks';
import { Variables } from '../../configs/appConfigs';
import LocalStorageService from '../../helpers/localStorageService';
import { setTokensInStorage } from '../../helpers/tokenService';
import { useIspMutator } from '../../isp/ispHooks';
import { ToastActionType } from '../../toast/context/toast.interface';
import { useToast } from '../../toast/context/toastContextHooks';
import FetchError from '../fetch/FetchError';
import { useHistory } from 'react-router-dom';
import type {
  FetchErrorType,
  FetchResponseType,
  UseQueryFetchContextType,
} from '../fetch/fetchInterfaces';
import handleRefreshToken from '../fetch/handleRefreshToken';
import { removeUserInfo } from '../../auth/userDataActions';

const useQueryFetch = <Data = FetchResponseType, Error = FetchErrorType>(
  url: string,
  query: string,
  getHeaders?: () => Record<string, string>,
  authRequired?: boolean,
): [(variables?: Record<string, any>) => Promise<Data>, UseQueryFetchContextType<Data, Error>] => {
  const [context, setContext] = useState<UseQueryFetchContextType<Data, Error>>({
    loading: false,
    data: null,
    error: null,
  });

  const toast = useToast();
  const dispatchAuth = useAuthMutate();
  const dispatchIspName = useIspMutator();
  const history = useHistory();
  let retrying = false;

  const requestHandler = async (variables?: Record<string, any>): Promise<Data> => {
    setContext({
      loading: true,
      data: null,
      error: null,
    });
    const fetchConfig = {
      method: 'POST',
      headers: getHeaders ? getHeaders() : {},
      body: JSON.stringify({ query: query, variables: variables ?? {} }),
    };

    try {
      const res = await fetch(url, fetchConfig);
      const resJson = await res.json();

      const ispName = res.headers.get('x-isp-name');
      if (ispName) dispatchIspName(ispName ? ispName.toUpperCase() : null);

      if (!res.ok) {
        const message = resJson?.errors?.[0]?.message ?? resJson.message;
        toast({
          message: message,
          options: { type: ToastActionType.ERROR, duration: 2000 },
        });
        throw new FetchError(res.status, resJson);
      }

      if (Array.isArray(resJson.errors) && resJson.errors.length > 0) {
        if (![401, 404].includes(resJson.errors[0]?.extensions?.statusCode)) {
          toast({
            message: resJson.errors[0]?.message,
            options: { type: ToastActionType.ERROR, duration: 2000 },
          });
        }

        throw new FetchError(res.status, resJson.errors[0]);
      }

      setContext({
        loading: false,
        data: resJson,
        error: null,
      });

      return resJson;
    } catch (err: any) {
      if (err?.body?.statusCode === 422 && err?.body?.message === 'INVALID_DEVICE_ID') {
        LocalStorageService.delete(Variables.accessToken);
        LocalStorageService.delete(Variables.refreshToken);
        LocalStorageService.delete(Variables.did);
        window.location.reload();
      }
      if (!retrying) {
        if (
          err.status === 401 ||
          err?.body?.extensions?.statusCode === 401 ||
          err?.body?.statusCode === 401
        ) {
          try {
            const tokens = await handleRefreshToken();
            setTokensInStorage(tokens.data.refreshToken.data);
            retrying = true;
            return await requestHandler(variables);
          } catch (error) {
            if (error.status === 401) {
              LocalStorageService.delete(Variables.accessToken);
              LocalStorageService.delete(Variables.refreshToken);

              dispatchAuth(removeUserInfo());

              history.replace('/preparation/logout');

              if (!authRequired) return await requestHandler(variables);
            }
          }
        }
        setContext({
          loading: false,
          data: null,
          error: err.body,
        });
        throw err;
      }
      throw err;
    }
  };

  return [requestHandler, context];
};

export default useQueryFetch;
